import React from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';

type PaginationMode = 'client' | 'server';

interface AlumlyDataGridProps {
  height?: number;
  rowHeight?: any;
  rows: any[];
  columns: any[];
  page?: number;
  pageSize?: number;
  rowsPerPageOptions?: any;
  loading?: boolean;
  paginationMode?: PaginationMode;
  rowCount?: number;
  onRowClick?: (params: any) => void;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  hideFooter?: boolean;
  [key: string]: any;
}

// Backed by Ant Design's Table. We keep the old MUI x-data-grid prop/column
// API (field / headerName / renderCell / valueGetter / width / flex) and adapt
// it to antd so the many list pages don't need to change.
const AlumlyDataGrid: React.FC<AlumlyDataGridProps> = ({
  rows = [],
  columns = [],
  page = 0,
  pageSize = 10,
  loading = false,
  paginationMode = 'client',
  rowCount = 0,
  onRowClick,
  onPageChange,
  hideFooter = false,
}) => {
  const antColumns: ColumnsType<any> = (columns || []).map((col: any) => {
    const column: any = {
      title: col.headerName ?? '',
      key: col.field,
      dataIndex: col.field,
      ellipsis: false,
    };
    if (col.width) column.width = col.width;
    column.render = (value: any, record: any, index: number) => {
      const params = {
        row: record,
        value: record?.[col.field],
        id: record?.id ?? index,
        field: col.field,
        api: {},
        formattedValue: record?.[col.field],
      };
      if (typeof col.renderCell === 'function') return col.renderCell(params);
      if (typeof col.valueGetter === 'function') return col.valueGetter(params);
      return record?.[col.field];
    };
    return column;
  });

  // If the columns are collectively wider than a typical content area, scroll
  // horizontally and keep each column's width (otherwise antd squeezes every
  // column to fit, wrapping headers into very tall blocks). Narrow tables fit.
  const totalWidth = (columns || []).reduce(
    (sum: number, c: any) => sum + (Number(c.width) || 160),
    0,
  );
  const needScroll = totalWidth > 1100;

  const pagination: any = hideFooter
    ? false
    : {
      pageSize,
      showSizeChanger: false,
      ...(paginationMode === 'server'
        ? {
          current: (page || 0) + 1,
          total: rowCount,
        }
        : {}),
      onChange: (p: number) => onPageChange?.(p - 1),
      hideOnSinglePage: false,
    };

  return (
    <Table
      rowKey={(record: any) => record?.id ?? record?.key ?? JSON.stringify(record).slice(0, 40)}
      dataSource={rows}
      columns={antColumns}
      loading={loading}
      pagination={pagination}
      size="middle"
      tableLayout={needScroll ? 'auto' : 'fixed'}
      scroll={needScroll ? { x: totalWidth } : undefined}
      locale={{ emptyText: 'Data not found' }}
      onRow={(record: any) => ({
        onClick: () => onRowClick?.({ row: record, id: record?.id }),
        style: { cursor: onRowClick ? 'pointer' : 'default' },
      })}
      style={{
        border: '1px solid #E4E4E7',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    />
  );
};

export default AlumlyDataGrid;
